/**
 * Chunked upload: every data-upload file travels in pieces, then the screen's normal endpoint
 * is called with `uploadId` instead of the file.
 *
 * nginx on the AI servers refuses any request body over 1 MB with an HTML 413 (no JSON, no
 * CORS headers, so the browser only sees "Failed to fetch"). Pieces of 512 KB never meet that
 * limit, whatever the file size:
 *
 *   POST data-upload/uploads                     {fileName, size, contentType, sha256, useCaseKey}
 *     -> {uploadId, chunkBytes, chunkCount}
 *   POST data-upload/uploads/:id/chunks/:index   multipart `chunk` (same index again replaces it)
 *   GET  data-upload/uploads/:id                 {received: number[], chunkCount, complete}
 *
 * An `uploadId` stays usable for 2 hours by several calls (Teachers: `extract-teachers` and
 * `onboarding/prefill`), so ids are cached per `File`. The routes only exist on the AI servers:
 * elsewhere callers keep sending the file itself (`isChunkedUploadAvailable`).
 */
import {dataUploadErrorCode, dataUploadErrorStatus} from 'common/helpers/dataUpload'
import type {DataUploadUseCaseKey} from 'common/helpers/dataUpload'
import {isSelectiveAiDomainHost} from 'common/helpers/selectiveAiDomain'
import {
  getChunkedDataUploadStatus,
  sendDataUploadChunk,
  startChunkedDataUpload,
} from 'setup/redux/Actions/DataUpload/dataUpload'

/** Anything `useDispatch()` returns; the actions are plain JS objects. */
type ApiDispatch = (action: any) => unknown
type Callback = (response: any) => void

/** Server default; used when the start answer leaves it out. */
export const DEFAULT_CHUNK_BYTES = 512 * 1024
/** Pieces in flight at once. */
const CHUNK_CONCURRENCY = 3
/** Attempts per piece before the upload fails. */
const CHUNK_ATTEMPTS = 3
/** The server keeps an upload for 2 hours; reuse an id for a little less than that. */
const UPLOAD_ID_TTL_MS = 110 * 60 * 1000
/** Codes that no retry can fix: the file itself was refused, or the upload is gone. */
const FINAL_CODES = new Set([
  'FILE_TYPE_NOT_ALLOWED',
  'FILE_SIGNATURE_MISMATCH',
  'TOO_LARGE',
  'EMPTY',
  'UPLOAD_NOT_FOUND',
  'FORBIDDEN',
])

/** Whether this server takes chunked uploads (the AI servers only). */
export const isChunkedUploadAvailable = (): boolean => isSelectiveAiDomainHost()

/**
 * A saga API action as a promise: resolves with what `onSuccess` got, rejects with what
 * `onFailure` got (the envelope, or an Error when the call got no answer).
 */
export const dispatchApi = <T = any>(
  dispatch: ApiDispatch,
  build: (onSuccess: Callback, onFailure: Callback) => unknown
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    dispatch(build(resolve, reject))
  })

export interface ChunkedUploadOptions {
  /** Lets the server refuse a type the screen can't read before any byte is sent. */
  useCaseKey?: DataUploadUseCaseKey
  /** 0..1, after each piece. */
  onProgress?: (fraction: number) => void
  /** Stops sending; the promise then rejects with an `AbortError`. */
  signal?: AbortSignal
}

interface UploadedFile {
  uploadId: string
  chunkBytes: number
  chunkCount: number
  expiresAt: number
}

const uploadedFiles = new WeakMap<File, UploadedFile>()

/** Drop a file's cached `uploadId` (a new one is made on the next upload). */
export const forgetUploadedFile = (file: File): void => {
  uploadedFiles.delete(file)
}

const abortError = () => new DOMException('Upload cancelled', 'AbortError')

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError'

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw abortError()
}

const asPositiveInt = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : null
}

/** Hex SHA-256 of the file, or undefined where Web Crypto is unavailable (plain http). */
const sha256Hex = async (file: File): Promise<string | undefined> => {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return undefined
  try {
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return undefined
  }
}

/** A failure worth sending the piece again for: no answer, a 5xx, or a damaged piece. */
const isRetryable = (error: unknown): boolean => {
  if (isAbortError(error)) return false
  const code = dataUploadErrorCode(error)
  if (code && FINAL_CODES.has(code)) return false
  const status = dataUploadErrorStatus(error)
  return status === null || status >= 500 || status === 408 || code === 'UPLOAD_INCOMPLETE'
}

const sendPiece = async (
  dispatch: ApiDispatch,
  file: File,
  upload: UploadedFile,
  index: number,
  signal?: AbortSignal
): Promise<void> => {
  const blob = file.slice(index * upload.chunkBytes, (index + 1) * upload.chunkBytes)
  for (let attempt = 1; ; attempt++) {
    throwIfAborted(signal)
    const form = new FormData()
    form.append('chunk', blob, `${index}.part`)
    try {
      await dispatchApi(dispatch, (ok, fail) =>
        sendDataUploadChunk(upload.uploadId, index, form, ok, fail)
      )
      return
    } catch (error) {
      if (attempt >= CHUNK_ATTEMPTS || !isRetryable(error)) throw error
      await new Promise((resolve) => setTimeout(resolve, 400 * attempt))
    }
  }
}

/** Sends `indexes` a few at a time; the first failure stops the rest. */
const sendPieces = async (
  dispatch: ApiDispatch,
  file: File,
  upload: UploadedFile,
  indexes: readonly number[],
  options: ChunkedUploadOptions,
  alreadySent = 0
): Promise<void> => {
  let next = 0
  let sent = alreadySent
  let failure: unknown = null
  const worker = async () => {
    while (failure === null && next < indexes.length) {
      const index = indexes[next++]
      try {
        await sendPiece(dispatch, file, upload, index, options.signal)
        sent += 1
        options.onProgress?.(Math.min(1, sent / upload.chunkCount))
      } catch (error) {
        failure ??= error
      }
    }
  }
  await Promise.all(
    Array.from({length: Math.min(CHUNK_CONCURRENCY, indexes.length)}, () => worker())
  )
  if (failure !== null) throw failure
}

/**
 * Upload `file` in pieces and resolve with its `uploadId`. A file already uploaded (and not
 * expired) resolves at once with the same id. Rejects with the failing call's envelope: a
 * refusal at the start (`FILE_TYPE_NOT_ALLOWED`, `TOO_LARGE`) or at piece 0
 * (`FILE_SIGNATURE_MISMATCH`) stops before the rest is sent.
 */
export const uploadFileInPieces = async (
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions = {}
): Promise<string> => {
  const cached = uploadedFiles.get(file)
  if (cached && cached.expiresAt > Date.now()) {
    options.onProgress?.(1)
    return cached.uploadId
  }
  throwIfAborted(options.signal)
  options.onProgress?.(0)

  const sha256 = await sha256Hex(file)
  throwIfAborted(options.signal)
  const started = await dispatchApi(dispatch, (ok, fail) =>
    startChunkedDataUpload(
      {
        fileName: file.name,
        size: file.size,
        ...(file.type ? {contentType: file.type} : {}),
        ...(sha256 ? {sha256} : {}),
        ...(options.useCaseKey ? {useCaseKey: options.useCaseKey} : {}),
      },
      ok,
      fail
    )
  )
  const result = started?.result ?? started
  const uploadId = result?.uploadId
  if (typeof uploadId !== 'string' || !uploadId) {
    throw {status: false, result: {code: 'UPLOAD_INCOMPLETE'}}
  }
  const chunkBytes = asPositiveInt(result.chunkBytes) ?? DEFAULT_CHUNK_BYTES
  const upload: UploadedFile = {
    uploadId,
    chunkBytes,
    chunkCount: asPositiveInt(result.chunkCount) ?? Math.max(1, Math.ceil(file.size / chunkBytes)),
    expiresAt: Date.now() + UPLOAD_ID_TTL_MS,
  }

  // Piece 0 alone first: the server checks the file's signature on it, so a renamed .exe is
  // refused about a second in, not after the whole file was sent.
  await sendPieces(dispatch, file, upload, [0], options)
  const rest = Array.from({length: upload.chunkCount - 1}, (_, i) => i + 1)
  await sendPieces(dispatch, file, upload, rest, options, 1)

  uploadedFiles.set(file, upload)
  return uploadId
}

/**
 * Re-send the pieces the server is missing (`GET data-upload/uploads/:id`), after a call
 * answered `UPLOAD_INCOMPLETE`.
 */
export const resumeUploadedFile = async (
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions = {}
): Promise<string> => {
  const upload = uploadedFiles.get(file)
  if (!upload) return uploadFileInPieces(dispatch, file, options)
  const status = await dispatchApi(dispatch, (ok, fail) =>
    getChunkedDataUploadStatus(upload.uploadId, ok, fail)
  )
  const result = status?.result ?? status
  const received = new Set<number>(Array.isArray(result?.received) ? result.received : [])
  const missing = Array.from({length: upload.chunkCount}, (_, i) => i).filter(
    (i) => !received.has(i)
  )
  if (missing.length > 0) {
    await sendPieces(dispatch, file, upload, missing, options, upload.chunkCount - missing.length)
  }
  return upload.uploadId
}

/**
 * Callback flavour of `withUploadedFile`, for screens built on onSuccess / onFailure: posts
 * `buildForm(uploadId)` once the pieces are sent, or `buildForm(null)` (the form then carries
 * the file itself) when `chunked` is false.
 */
export const sendFileForm = (
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions & {chunked: boolean},
  buildForm: (uploadId: string | null) => FormData,
  action: (body: FormData, onSuccess: Callback, onFailure: Callback) => unknown,
  onSuccess: Callback,
  onFailure: Callback
): void => {
  const post = (uploadId: string | null) =>
    dispatchApi(dispatch, (ok, fail) => action(buildForm(uploadId), ok, fail))
  const pending = options.chunked ? withUploadedFile(dispatch, file, options, post) : post(null)
  pending.then(onSuccess, onFailure)
}

/**
 * Upload `file` in pieces, then run `call` with its `uploadId`. When the call answers
 * `UPLOAD_INCOMPLETE` the missing pieces are sent again; when it answers `UPLOAD_NOT_FOUND`
 * (expired) the file is uploaded again. Either way `call` runs once more; a second failure is
 * the caller's to show.
 */
export const withUploadedFile = async <T>(
  dispatch: ApiDispatch,
  file: File,
  options: ChunkedUploadOptions,
  call: (uploadId: string) => Promise<T>
): Promise<T> => {
  const uploadId = await uploadFileInPieces(dispatch, file, options)
  try {
    return await call(uploadId)
  } catch (error) {
    const code = dataUploadErrorCode(error)
    if (code === 'UPLOAD_INCOMPLETE') {
      return call(await resumeUploadedFile(dispatch, file, options))
    }
    if (code === 'UPLOAD_NOT_FOUND') {
      forgetUploadedFile(file)
      return call(await uploadFileInPieces(dispatch, file, options))
    }
    throw error
  }
}
